import styled from '@emotion/styled';
import { Box } from '@mui/material';
import ToolbarMui from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { AppState } from '../../state';
import ColumnChooser from './ColumnChooser';
import { useDataGridFlexSales } from './DataGridFlexSalesProvider';
import Export from './Export';
import { CurrentDateWarning } from './alerts';

const ToolbarStyled = styled(ToolbarMui)`
  min-height: 56px;
  background: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.36)}, ${alpha(
          theme.palette.common.white,
          0.06
        )})`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(
          theme.palette.secondary.main,
          0.16
        )})`};
  justify-content: space-between;
  color: ${({ theme }) => theme.palette.text.primary};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-bottom: 0;
  border-radius: ${({ theme }) => theme.spacing(2.5, 2.5, 0, 0)};

  button {
    color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main};
    background-color: ${({ theme }) => alpha(theme.palette.background.paper, 0.72)};

    &:hover {
      background-color: ${({ theme }) =>
        alpha(
          theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
          0.1
        )};
    }
  }

  &.Toolbar-toolbar {
    border: none;
  }

  @media (min-width: 600px) {
    padding-left: ${props => props.theme.spacing(2)};
    padding-right: ${props => props.theme.spacing(2)};
  }
`;

const DataGridAlerts = () => {
  const { handler } = useDataGridFlexSales();
  const [filters, setFilters] = useState(handler.filters);

  useEffect(() => {
    const listenerFilters = handler.addEventListener('filters', ({ filters }) =>
      setFilters(filters)
    );

    return () => {
      listenerFilters.dispose();
    };
  }, [handler]);

  return <CurrentDateWarning filters={filters} />;
};

const DataGridTitle = () => {
  const { handler } = useDataGridFlexSales();
  const [title, setTitle] = useState(handler.titles.tableTitle);

  useEffect(() => {
    const listenerTitle = handler.addEventListener('title', ({ titles }) =>
      setTitle(titles.tableTitle)
    );

    return () => {
      listenerTitle.dispose();
    };
  }, [handler]);

  return <Typography variant="subtitle1">{title}</Typography>;
};

export const Toolbar = ({
  columnChooserDisabledSelector,
}: {
  columnChooserDisabledSelector?: (state: AppState) => boolean;
}) => (
  <ToolbarStyled variant="dense">
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <DataGridTitle />
      <DataGridAlerts />
    </Box>
    <Box data-tour="report-grid-actions" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ColumnChooser disabledSelector={columnChooserDisabledSelector} />
      <Export />
    </Box>
  </ToolbarStyled>
);
